import { Typography } from '@mui/material'
import { useCallback, useMemo } from 'react'

import { Col, FieldSet, Legend, Row } from '@/components/Layout'
import { SelectFormField, TextFormField } from '@/components/UberForm'
import {
	DEFAULT_ODCS_STATUS,
	ODCS_STATUS_I18N_KEYS,
	ODCS_STATUS_VALUES,
} from '@/features/folder-odcs/odcsStatusOptions'
import { useEffectiveOdcsTenant } from '@/features/folder-odcs/useEffectiveOdcsTenant'
import { useAppContext } from '@/hooks'
import { DescriptionFieldWithFullscreen } from '@/pages/User/pages/Home/pages/ViewDetailV2/components/DescriptionFieldWithFullscreen'
import { StatusEnum } from '@/rtkApi/mddApiSliceGeneratedV2'
import { FolderDataForm } from '@/store/modules/folder/types'

type FolderOdcsFieldsProps = {
	form: FolderDataForm
	isReadOnly: boolean
	nodeId?: number
	onOdcsFieldChange: (patch: Partial<FolderDataForm>) => void
	preferWorkingData: boolean
}

export const FolderOdcsFields = ({
	form,
	isReadOnly,
	nodeId,
	onOdcsFieldChange,
	preferWorkingData,
}: FolderOdcsFieldsProps) => {
	const { t } = useAppContext()

	const statusOptions = useMemo(
		() =>
			ODCS_STATUS_VALUES.map((value) => ({
				value,
				label: t(ODCS_STATUS_I18N_KEYS[value]),
			})),
		[t],
	)

	const effectiveTenant = useEffectiveOdcsTenant(
		nodeId,
		form.odcsTenant,
		preferWorkingData,
	)

	const handleMarkdownChange = useCallback(
		(field: 'odcsPurpose' | 'odcsUsage' | 'odcsLimitations', value: string) => {
			onOdcsFieldChange({ [field]: value })
		},
		[onOdcsFieldChange],
	)

	const tenantHelperText = effectiveTenant.inherited
		? t('ODCS_TENANT_INHERITED', [
				effectiveTenant.effective,
				effectiveTenant.fromFolderName ?? '',
			])
		: form.odcsTenant?.trim()
			? undefined
			: t('ODCS_TENANT_INHERIT_HINT')

	return (
		<FieldSet className="mt-5">
			<Legend>{t('FOLDER_ODCS_SECTION')}</Legend>
			<Row>
				<Col size="medium">
					<SelectFormField
						name="odcsStatus"
						title={t('ODCS_STATUS')}
						options={statusOptions}
						initialValue={form.odcsStatus ?? DEFAULT_ODCS_STATUS}
						allowEmpty={false}
						isDisabled={isReadOnly}
					/>
				</Col>
				<Col size="medium">
					<TextFormField
						name="odcsTenant"
						title={t('ODCS_TENANT')}
						initialValue={form.odcsTenant ?? ''}
						disabled={isReadOnly}
						tooltip={t('ODCS_TENANT_INHERIT_HINT')}
					/>
					{tenantHelperText && (
						<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
							{tenantHelperText}
						</Typography>
					)}
				</Col>
			</Row>

			<div className="mt-4 flex flex-col gap-4">
				<DescriptionFieldWithFullscreen
					title={t('ODCS_PURPOSE')}
					value={form.odcsPurpose ?? ''}
					onChange={(value) => handleMarkdownChange('odcsPurpose', value)}
					readOnly={isReadOnly}
				/>
				<DescriptionFieldWithFullscreen
					title={t('ODCS_USAGE')}
					value={form.odcsUsage ?? ''}
					onChange={(value) => handleMarkdownChange('odcsUsage', value)}
					readOnly={isReadOnly}
				/>
				<DescriptionFieldWithFullscreen
					title={t('ODCS_LIMITATIONS')}
					value={form.odcsLimitations ?? ''}
					onChange={(value) => handleMarkdownChange('odcsLimitations', value)}
					readOnly={isReadOnly}
				/>
			</div>
		</FieldSet>
	)
}

export const isOdcsFormField = (name: string): name is keyof FolderDataForm =>
	name === 'odcsStatus' ||
	name === 'odcsTenant' ||
	name === 'odcsPurpose' ||
	name === 'odcsUsage' ||
	name === 'odcsLimitations'

export const mapOdcsFormPatch = (
	item: Partial<FolderDataForm>,
): Partial<FolderDataForm> => {
	const patch: Partial<FolderDataForm> = {}

	if ('odcsStatus' in item) {
		patch.odcsStatus = item.odcsStatus as StatusEnum
	}
	if ('odcsTenant' in item) {
		patch.odcsTenant = item.odcsTenant
	}
	if ('odcsPurpose' in item) {
		patch.odcsPurpose = item.odcsPurpose
	}
	if ('odcsUsage' in item) {
		patch.odcsUsage = item.odcsUsage
	}
	if ('odcsLimitations' in item) {
		patch.odcsLimitations = item.odcsLimitations
	}

	return patch
}
